import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./CostDashboard.css";

interface CostSummary {
  totalCost: number;
  totalDisputes: number;
  averageCostPerDispute: number;
  byType: {
    rain: {
      totalCost: number;
      disputeCount: number;
      averageCost: number;
      disputeProcessingCost: number;
      evidenceAnalysisCost: number;
    };
    tomi: {
      totalCost: number;
      disputeCount: number;
      averageCost: number;
      disputeProcessingCost: number;
      evidenceAnalysisCost: number;
    };
  };
  evidenceBreakdown: {
    totalEvidenceItems: number;
    totalEvidenceCost: number;
    averageCostPerEvidence: number;
    byType: {
      [key: string]: {
        count: number;
        totalCost: number;
      };
    };
  };
  timeline: Array<{
    date: string;
    cost: number;
    disputes: number;
  }>;
}

interface CostDashboardProps {
  token: string;
  disputeType: "all" | "rain" | "tomi";
  dateFrom?: string;
  dateTo?: string;
}

const CostDashboard: React.FC<CostDashboardProps> = ({
  token,
  disputeType,
  dateFrom,
  dateTo,
}) => {
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [localDateFrom, setLocalDateFrom] = useState(dateFrom || "");
  const [localDateTo, setLocalDateTo] = useState(dateTo || "");

  const fetchCostSummary = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const params: any = {
        disputeType,
      };
      if (localDateFrom) params.dateFrom = localDateFrom;
      if (localDateTo) params.dateTo = localDateTo;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/costs/summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      setSummary(response.data.summary);
    } catch (error) {
      console.error("Error fetching cost summary:", error);
    } finally {
      setLoading(false);
    }
  }, [token, disputeType, localDateFrom, localDateTo]);

  useEffect(() => {
    fetchCostSummary();
  }, [fetchCostSummary]);

  const handleApplyDateFilter = () => {
    fetchCostSummary();
  };

  const handleClearDateFilter = () => {
    setLocalDateFrom("");
    setLocalDateTo("");
  };

  if (loading && !summary) {
    return (
      <div className="cost-dashboard">
        <div className="cost-loading">Loading cost data...</div>
      </div>
    );
  }

  if (!summary) return null;

  const evidenceTypes = Object.keys(summary.evidenceBreakdown.byType).sort(
    (a, b) =>
      summary.evidenceBreakdown.byType[b].totalCost -
      summary.evidenceBreakdown.byType[a].totalCost
  );

  return (
    <div className="cost-dashboard">
      <div className="cost-dashboard-header">
        <h2 className="cost-dashboard-title">Cost Dashboard</h2>
        <div className="cost-date-filter">
          <input
            type="date"
            className="cost-date-input"
            value={localDateFrom}
            onChange={(e) => setLocalDateFrom(e.target.value)}
            placeholder="From"
          />
          <span className="date-separator">to</span>
          <input
            type="date"
            className="cost-date-input"
            value={localDateTo}
            onChange={(e) => setLocalDateTo(e.target.value)}
            placeholder="To"
          />
          <button
            className="cost-filter-btn apply"
            onClick={handleApplyDateFilter}
          >
            Apply
          </button>
          {(localDateFrom || localDateTo) && (
            <button
              className="cost-filter-btn clear"
              onClick={handleClearDateFilter}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="cost-dashboard-body">
        {/* Total Summary */}
        <div className="cost-total-section">
          <div className="cost-stat-card total">
            <div className="cost-stat-label">Total Cost</div>
            <div className="cost-stat-value total-value">
              ${summary.totalCost.toFixed(4)}
            </div>
          </div>
          <div className="cost-stat-card">
            <div className="cost-stat-label">Total Disputes</div>
            <div className="cost-stat-value">{summary.totalDisputes}</div>
          </div>
          <div className="cost-stat-card">
            <div className="cost-stat-label">Avg per Dispute</div>
            <div className="cost-stat-value">
              ${summary.averageCostPerDispute.toFixed(4)}
            </div>
          </div>
        </div>

        {/* Rain vs Tomi Breakdown */}
        <div className="cost-type-breakdown">
          <div className="cost-type-card rain-card">
            <div className="cost-type-header">
              <span className="cost-type-name">Rain</span>
            </div>
            <div className="cost-type-total">
              ${summary.byType.rain.totalCost.toFixed(4)}
            </div>
            <div className="cost-type-details">
              <div className="cost-type-count">
                {summary.byType.rain.disputeCount} disputes
              </div>
              <div className="cost-type-avg">
                Avg: ${summary.byType.rain.averageCost.toFixed(4)}
              </div>
            </div>
            <div className="cost-breakdown-items">
              <div className="cost-breakdown-item">
                <span className="cost-breakdown-label">Processing:</span>
                <span className="cost-breakdown-value">
                  ${summary.byType.rain.disputeProcessingCost.toFixed(4)}
                </span>
              </div>
              <div className="cost-breakdown-item">
                <span className="cost-breakdown-label">Evidence:</span>
                <span className="cost-breakdown-value">
                  ${summary.byType.rain.evidenceAnalysisCost.toFixed(4)}
                </span>
              </div>
            </div>
          </div>

          <div className="cost-type-card tomi-card">
            <div className="cost-type-header">
              <span className="cost-type-name">Tomi</span>
            </div>
            <div className="cost-type-total">
              ${summary.byType.tomi.totalCost.toFixed(4)}
            </div>
            <div className="cost-type-details">
              <div className="cost-type-count">
                {summary.byType.tomi.disputeCount} disputes
              </div>
              <div className="cost-type-avg">
                Avg: ${summary.byType.tomi.averageCost.toFixed(4)}
              </div>
            </div>
            <div className="cost-breakdown-items">
              <div className="cost-breakdown-item">
                <span className="cost-breakdown-label">Processing:</span>
                <span className="cost-breakdown-value">
                  ${summary.byType.tomi.disputeProcessingCost.toFixed(4)}
                </span>
              </div>
              <div className="cost-breakdown-item">
                <span className="cost-breakdown-label">Evidence:</span>
                <span className="cost-breakdown-value">
                  ${summary.byType.tomi.evidenceAnalysisCost.toFixed(4)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Evidence Analysis Breakdown */}
        <div className="cost-evidence-section">
          <h3 className="cost-section-title">Evidence Analysis Costs</h3>
          <div className="cost-evidence-summary">
            <div className="evidence-summary-item">
              <span className="evidence-summary-label">Total Items:</span>
              <span className="evidence-summary-value">
                {summary.evidenceBreakdown.totalEvidenceItems}
              </span>
            </div>
            <div className="evidence-summary-item">
              <span className="evidence-summary-label">Total Cost:</span>
              <span className="evidence-summary-value">
                ${summary.evidenceBreakdown.totalEvidenceCost.toFixed(4)}
              </span>
            </div>
            <div className="evidence-summary-item">
              <span className="evidence-summary-label">Avg per Item:</span>
              <span className="evidence-summary-value">
                ${summary.evidenceBreakdown.averageCostPerEvidence.toFixed(4)}
              </span>
            </div>
          </div>

          <div className="cost-evidence-types">
            {evidenceTypes.map((type) => {
              const typeData = summary.evidenceBreakdown.byType[type];
              return (
                <div key={type} className="evidence-type-item">
                  <div className="evidence-type-header">
                    <span className="evidence-type-name">
                      {type.replace(/_/g, " ").toUpperCase()}
                    </span>
                    <span className="evidence-type-count">
                      ({typeData.count})
                    </span>
                  </div>
                  <div className="evidence-type-cost">
                    ${typeData.totalCost.toFixed(4)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline Toggle */}
        {summary.timeline.length > 0 && (
          <div className="cost-timeline-section">
            <button
              className="cost-timeline-toggle"
              onClick={() => setShowTimeline(!showTimeline)}
            >
              {showTimeline ? "Hide" : "Show"} Detailed Timeline
              <span className="toggle-icon">{showTimeline ? "▲" : "▼"}</span>
            </button>

            {showTimeline && (
              <div className="cost-timeline">
                <table className="timeline-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Disputes</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.timeline.map((day) => (
                      <tr key={day.date}>
                        <td>{new Date(day.date).toLocaleDateString()}</td>
                        <td>{day.disputes}</td>
                        <td>${day.cost.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CostDashboard;





